import { useQuery } from '@tanstack/react-query'
import { CalendarDays, FileSpreadsheet, Layers, Plus, Tags } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { yearsQuery } from '@/features/academic/api'
import { Can } from '@/features/auth/guards'
import { examsQuery } from '@/features/exams/api'
import { ExamStatusBadge } from '@/features/exams/components/ExamBadges'
import { ExamDialog } from '@/features/exams/components/ExamDialog'
import { ExamTypesDialog } from '@/features/exams/components/ExamTypesDialog'
import { ProgressMeter } from '@/features/exams/components/ProgressMeter'
import { examStatusLabel } from '@/features/exams/status'
import type { Exam, ExamStatus } from '@/features/exams/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDate } from '@/utils/format'

const FILTERS = ['academicYearId', 'status'] as const
const STATUSES = Object.keys(examStatusLabel) as ExamStatus[]

export function ExamsListPage() {
  const { t } = useTranslation('exams')
  useDocumentTitle('Exams')
  const { state, query, update, setPage } = useListParams(FILTERS, { sort: 'startsOn', perPage: 12 })
  const exams = useQuery(examsQuery(query))
  const years = useQuery(yearsQuery())
  const [dialog, setDialog] = useState<'exam' | 'types' | null>(null)
  const close = (open: boolean) => (open ? undefined : setDialog(null))

  return (
    <>
      <PageHeader
        title={t('Exams')}
        description={t('Plan assessments, follow mark entry and publish results.')}
        breadcrumbs={[{ label: t('Academics') }, { label: t('Examinations') }]}
        homeHref="/admin"
        actions={
          <Can permission="exams.manage">
            <Button variant="secondary" leftIcon={<Tags />} onClick={() => setDialog('types')}>
              {t('Exam types')}
            </Button>
            <Button leftIcon={<Plus />} onClick={() => setDialog('exam')}>
              {t('New exam')}
            </Button>
          </Can>
        }
      />

      <Card className="mb-5">
        <FilterBar>
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Search exams…')}
            label={t('Search exams')}
          />
          <Select
            aria-label={t('Academic year')}
            className="sm:w-auto sm:min-w-48"
            placeholder={t('All years')}
            value={state.filters.academicYearId ?? ''}
            onChange={(e) => update({ academicYearId: e.target.value })}
            options={(years.data ?? []).map((y) => ({ value: y.id, label: y.name }))}
          />
          <Select
            aria-label={t('Status')}
            className="sm:w-auto sm:min-w-48"
            placeholder={t('Any status')}
            value={state.filters.status ?? ''}
            onChange={(e) => update({ status: e.target.value })}
            options={STATUSES.map((s) => ({ value: s, label: t(examStatusLabel[s]) }))}
          />
        </FilterBar>
      </Card>

      {exams.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i} className="space-y-3 p-5">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-2 w-full" />
            </Card>
          ))}
        </div>
      ) : exams.error ? (
        <Card>
          <ErrorState onRetry={() => void exams.refetch()} />
        </Card>
      ) : exams.data.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileSpreadsheet />}
            title={
              state.search || Object.keys(state.filters).length ? t('No exams match') : t('No exams yet')
            }
            description={t(
              'Create an exam, choose the classes sitting it, then add papers from the curriculum.',
            )}
            action={
              <Can permission="exams.manage">
                <Button leftIcon={<Plus />} onClick={() => setDialog('exam')}>
                  {t('New exam')}
                </Button>
              </Can>
            }
          />
        </Card>
      ) : (
        <>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {exams.data.data.map((exam) => (
              <li key={exam.id}>
                <ExamCard exam={exam} />
              </li>
            ))}
          </ul>
          <Pagination className="mt-5" meta={exams.data.meta} onPageChange={setPage} />
        </>
      )}

      {dialog === 'exam' ? <ExamDialog open onOpenChange={close} /> : null}
      {dialog === 'types' ? <ExamTypesDialog open onOpenChange={close} /> : null}
    </>
  )
}

function ExamCard({ exam }: { exam: Exam }) {
  const { t } = useTranslation('exams')
  return (
    <Link to={`/admin/exams/${exam.id}`} className="block h-full rounded-card focus-visible:outline-none">
      <Card className="flex h-full flex-col p-5 transition-shadow hover:shadow-raised">
        <div className="flex items-start gap-3">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600 [&_svg]:size-5"
            aria-hidden="true"
          >
            <FileSpreadsheet />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-ink">{exam.name}</h2>
            <p className="text-sm text-muted">
              {exam.examType.name} · {exam.academicYear.name}
            </p>
          </div>
          <ExamStatusBadge status={exam.status} size="sm" />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-soft">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="size-4 text-subtle" aria-hidden="true" />
            {formatDate(exam.startsOn)} – {formatDate(exam.endsOn)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Layers className="size-4 text-subtle" aria-hidden="true" />
            {t('{{count}} classes', { count: exam.classes.length })} ·{' '}
            {t('{{count}} papers', { count: exam.paperCount })}
          </span>
        </div>
        <ProgressMeter
          className="mt-auto pt-4"
          entered={exam.marksEntered}
          expected={exam.marksExpected}
          label={t('Marks entered')}
        />
      </Card>
    </Link>
  )
}
